import React from 'react';
import SectionDivider from '../components/SectionDivider';
import { useTranslations } from '../context/TranslationContext';

const Privacy: React.FC = () => {
  const { t } = useTranslations();

  const sections = [
    { id: 'introduction', label: t('toc_introduction') },
    { id: 'contact', label: t('contact_info_title') },
    { id: 'data-collection', label: t('types_data_collected_title') },
    { id: 'processing', label: t('processing_mode_title') },
    { id: 'detailed-info', label: t('detailed_info_title') },
    { id: 'rights', label: t('gdpr_rights_title') },
    { id: 'more-info', label: t('additional_info_title') },
    { id: 'definitions', label: t('definitions_title') },
  ];

  const introHighlights = [
    {
      title: t('intro_what_policy_about_title'),
      description: t('intro_what_policy_about_text'),
    },
    {
      title: t('intro_what_personal_data_title'),
      description: t('intro_what_personal_data_text'),
    },
  ];

  const personalDataItems = [
    t('data_type_first_name'),
    t('data_type_last_name'),
    t('data_type_email'),
    t('data_type_trackers'),
    t('data_type_usage_data'),
  ];

  const processingDetails = [
    {
      title: t('processing_methods_title'),
      description: t('processing_methods_text'),
    },
    {
      title: t('processing_place_title'),
      description: t('processing_place_text'),
    },
    {
      title: t('processing_retention_time_title'),
      description: t('processing_retention_time_text'),
    },
  ];

  const serviceDetails = [
    {
      title: t('analytics_title'),
      description: t('analytics_text'),
      additional: [
        {
          label: t('personal_data_processed'),
          value: `${t('data_type_trackers')}; ${t('data_type_usage_data')}`,
        },
        {
          label: t('place_of_processing'),
          value: t('united_states'),
        },
      ],
      subTitle: t('google_analytics_title'),
      subDescription: t('google_analytics_text'),
    },
    {
      title: t('contact_form_title'),
      description: t('contact_form_text'),
      additional: [
        {
          label: t('personal_data_processed'),
          value: `${t('data_type_email')}; ${t('data_type_first_name')}; ${t('data_type_last_name')}`,
        },
      ],
    },
  ];

  const gdprRights = [
    {
      title: t('gdpr_withdraw_consent_title'),
      description: t('gdpr_withdraw_consent_text'),
    },
    {
      title: t('gdpr_object_processing_title'),
      description: t('gdpr_object_processing_text'),
    },
    {
      title: t('gdpr_access_data_title'),
      description: t('gdpr_access_data_text'),
    },
    {
      title: t('gdpr_verify_rectification_title'),
      description: t('gdpr_verify_rectification_text'),
    },
    {
      title: t('gdpr_delete_data_title'),
      description: t('gdpr_delete_data_text'),
    },
  ];

  const additionalInformation = [
    {
      title: t('legal_action_title'),
      description: t('legal_action_text'),
    },
    {
      title: t('system_logs_maintenance_title'),
      description: t('system_logs_maintenance_text'),
    },
    {
      title: t('changes_privacy_policy_title'),
      description: t('changes_privacy_policy_text'),
    },
  ];

  const definitions = [
    {
      title: t('definition_personal_data_title'),
      description: t('definition_personal_data_text'),
    },
    {
      title: t('definition_usage_data_title'),
      description: t('definition_usage_data_text'),
    },
    {
      title: t('definition_cookie_title'),
      description: t('definition_cookie_text'),
    },
    {
      title: t('definition_tracker_title'),
      description: t('definition_tracker_text'),
    },
  ];

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-void-blue text-white">

      <section id="hero" className="relative pt-32 pb-12 md:pb-16 bg-void-blue">
        <div className="w-[80%] mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-[2.25fr,1fr] lg:items-start">
            <div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-5.5xl font-bold leading-tight text-white mb-6">
                {t('privacy_policy_title')}
              </h1>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-6">
                {t('privacy_intro_text_1')}
              </p>
              <p className="text-lg text-gray-400 leading-relaxed">
                {t('privacy_intro_text_2')}{' '}
                <a
                  href="mailto:info@legisratio.it"
                  className="text-mystic-violet hover:text-cosmic-teal transition-colors"
                >
                  info@legisratio.it
                </a>
              </p>
              <div className="mt-8 flex items-center space-x-3 text-sm font-medium uppercase tracking-[0.2em] text-gray-500">
                <span className="h-1.5 w-1.5 rounded-full bg-mystic-violet" aria-hidden="true" />
                <span>{t('last_updated')}</span>
              </div>
            </div>
            <aside className="glass-panel rounded-2xl shadow-sm p-6 lg:sticky lg:top-32 border border-white/10">
              <p className="text-sm font-semibold text-gray-400 uppercase tracking-[0.2em] mb-4">
                {t('table_of_contents')}
              </p>
              <ul className="space-y-3">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="flex items-center justify-between rounded-xl border border-transparent px-4 py-3 text-sm font-semibold text-gray-300 transition-colors duration-200 hover:border-mystic-violet/30 hover:bg-white/5 hover:text-mystic-violet"
                    >
                      <span>{section.label}</span>
                      <span aria-hidden="true">↗</span>
                    </a>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </section>

      <SectionDivider />

      <section id="introduction" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="font-display text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('toc_introduction')}
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {introHighlights.map((item) => (
                <div key={item.title} className="glass-panel rounded-xl p-6 shadow-sm border border-white/10">
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-base md:text-lg text-gray-300 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      <section id="contact" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="font-display text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('contact_info_title')}
              </h2>
            </div>
            <div className="glass-panel rounded-2xl shadow-sm p-8 space-y-6 border border-white/10">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-400 mb-3">
                  {t('contact_address')}
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  {t('address_line_1')}
                  <br />
                  {t('address_line_2')}
                  <br />
                  {t('address_line_3')}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-400 mb-3">
                  {t('contact_email')}
                </p>
                <a
                  href="mailto:info@legisratio.it"
                  className="inline-flex items-center text-lg font-semibold text-mystic-violet hover:text-cosmic-teal"
                >
                  info@legisratio.it
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      <section id="data-collection" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('types_data_collected_title')}
              </h2>
            </div>
            <div className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                {t('types_data_collected_intro')}
              </p>
              <ul className="grid gap-3 sm:grid-cols-2">
                {personalDataItems.map((item) => (
                  <li key={item} className="flex items-start rounded-xl border border-white/10 bg-white/5 p-4 text-base text-gray-200">
                    <span className="mt-1 mr-3 h-2 w-2 rounded-full bg-mystic-violet" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-lg text-gray-300 leading-relaxed">
                {t('types_data_collected_outro')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      <section id="processing" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('processing_mode_title')}
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {processingDetails.map((detail) => (
                <div key={detail.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-white mb-3">{detail.title}</h3>
                  <p className="text-base md:text-lg text-gray-300 leading-relaxed">{detail.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      <section id="detailed-info" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('detailed_info_title')}
              </h2>
            </div>
            <div className="space-y-8">
              <p className="text-lg text-gray-300 leading-relaxed">
                {t('detailed_info_intro')}
              </p>
              {serviceDetails.map((service) => (
                <div key={service.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm space-y-4">
                  <div>
                    <h3 className="text-2xl font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  {service.subTitle && (
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">{service.subTitle}</h4>
                      <p className="text-base text-gray-300 leading-relaxed">{service.subDescription}</p>
                    </div>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2">
                    {service.additional.map((item) => (
                      <div key={`${service.title}-${item.label}`} className="rounded-xl border border-white/10 bg-mystic-violet/10 p-4">
                        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-mystic-violet mb-2">
                          {item.label}
                        </p>
                        <p className="text-base text-gray-200 leading-relaxed">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      <section id="rights" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('gdpr_rights_title')}
              </h2>
            </div>
            <div className="space-y-8">
              <p className="text-lg text-gray-300 leading-relaxed">
                {t('gdpr_rights_intro')}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                {gdprRights.map((right) => (
                  <div key={right.title} className="rounded-xl border border-white/10 bg-white/5 p-5 shadow-sm">
                    <h3 className="text-lg font-semibold text-white mb-2">{right.title}</h3>
                    <p className="text-base text-gray-300 leading-relaxed">{right.description}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-mystic-violet/20 bg-mystic-violet/5 p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  {t('gdpr_how_to_exercise_rights_title')}
                </h3>
                <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                  {t('gdpr_how_to_exercise_rights_text')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      <section id="more-info" className="pt-1 md:pt-1 pb-12 md:pb-14 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('additional_info_title')}
              </h2>
            </div>
            <div className="space-y-6">
              {additionalInformation.map((item) => (
                <div key={item.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-base md:text-lg text-gray-300 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="definitions" className="pt-1 md:pt-1 pb-24 md:pb-28 bg-void-blue">
        <div className="w-full px-6 md:w-[80%] md:px-0 mx-auto max-w-8xl">
          <div className="grid gap-10 lg:grid-cols-[1fr,3fr]">
            <div>
              <h2 className="text-3xl md:text-4.5xl font-bold text-white sticky top-32">
                {t('definitions_title')}
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {definitions.map((definition) => (
                <div key={definition.title} className="rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm">
                  <h3 className="text-xl font-semibold text-white mb-3">{definition.title}</h3>
                  <p className="text-base text-gray-300 leading-relaxed">{definition.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;